import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical'

export type CTAButtonAlignment = 'left' | 'center' | 'right'

export interface CTAButtonPayload {
  text: string
  url: string
  align?: CTAButtonAlignment
}

export type SerializedCTAButtonNode = Spread<
  {
    text: string
    url: string
    align: CTAButtonAlignment
  },
  SerializedLexicalNode
>

function convertCTAButtonElement(domNode: HTMLElement): DOMConversionOutput | null {
  try {
    // Find anchor elements that are direct children and have the 'button' class
    const children = Array.from(domNode.children)
    const anchor = children.find(
      child => child.tagName === 'A' && child.classList.contains('button')
    ) as HTMLAnchorElement | null
    
    if (anchor) {
      const text = anchor.textContent || 'Click here'
      const url = anchor.getAttribute('href') || ''
      // Parse alignment from style
      const textAlign = domNode.style.textAlign as CTAButtonAlignment
      const align: CTAButtonAlignment = ['left', 'center', 'right'].includes(textAlign) ? textAlign : 'left'
      const node = $createCTAButtonNode({ text, url, align })
      return { node }
    }
  } catch (err) {
    console.error('Error converting CTA button element:', err)
  }
  return null
}

export class CTAButtonNode extends DecoratorNode<JSX.Element> {
  __text: string
  __url: string
  __align: CTAButtonAlignment

  static getType(): string {
    return 'cta-button'
  }

  static clone(node: CTAButtonNode): CTAButtonNode {
    return new CTAButtonNode(node.__text, node.__url, node.__align, node.__key)
  }

  constructor(text: string, url: string, align: CTAButtonAlignment = 'left', key?: NodeKey) {
    super(key)
    this.__text = text
    this.__url = url
    this.__align = align
  }

  createDOM(): HTMLElement {
    const div = document.createElement('div')
    div.style.textAlign = this.__align
    div.style.margin = '16px 0'
    return div
  }

  updateDOM(prevNode: CTAButtonNode, dom: HTMLElement): boolean {
    if (prevNode.__align !== this.__align) {
      dom.style.textAlign = this.__align
    }
    return false
  }

  isInline(): false {
    return false
  }

  getTextContent(): string {
    return this.__text
  }

  static importJSON(serializedNode: SerializedCTAButtonNode): CTAButtonNode {
    const { text, url, align } = serializedNode
    return $createCTAButtonNode({ text, url, align })
  }

  exportJSON(): SerializedCTAButtonNode {
    return {
      type: 'cta-button',
      version: 1,
      text: this.__text,
      url: this.__url,
      align: this.__align,
    }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        // Only match divs where a.button is a direct child (not nested in other elements)
        // This prevents matching wrapper divs that contain the button somewhere inside
        const children = Array.from(domNode.children)
        const hasDirectButtonChild = children.some(
          child => child.tagName === 'A' && child.classList.contains('button')
        )
        if (hasDirectButtonChild) {
          return {
            conversion: convertCTAButtonElement,
            priority: 1,
          }
        }
        return null
      },
    }
  }

  exportDOM(): DOMExportOutput {
    const container = document.createElement('div')
    container.style.textAlign = this.__align

    const anchor = document.createElement('a')
    anchor.href = this.__url
    anchor.className = 'button font-14'
    anchor.textContent = this.__text

    container.appendChild(anchor)

    return { element: container }
  }

  getText(): string {
    return this.__text
  }

  getUrl(): string {
    return this.__url
  }

  getAlign(): CTAButtonAlignment {
    return this.__align
  }

  setText(text: string): void {
    const writable = this.getWritable()
    writable.__text = text
  }

  setUrl(url: string): void {
    const writable = this.getWritable()
    writable.__url = url
  }

  setAlign(align: CTAButtonAlignment): void {
    const writable = this.getWritable()
    writable.__align = align
  }

  decorate(): JSX.Element {
    return (
      <CTAButtonComponent
        text={this.__text}
        url={this.__url}
        align={this.__align}
        nodeKey={this.__key}
      />
    )
  }
}

// Component rendered in the editor
interface CTAButtonComponentProps {
  text: string
  url: string
  align: CTAButtonAlignment
  nodeKey: NodeKey
}

export function CTAButtonComponent({ text, url, align, nodeKey }: CTAButtonComponentProps) {
  const alignmentClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align]

  return (
    <div
      className={`cta-button-wrapper my-4 ${alignmentClass}`}
      data-node-key={nodeKey}
    >
      <span
        className="cta-button-inner inline-block cursor-pointer rounded-md px-6 py-3 font-semibold text-black transition-opacity hover:opacity-80"
        style={{ backgroundColor: '#c6f648' }}
        title={`Link: ${url} (click to edit)`}
      >
        {text}
      </span>
    </div>
  )
}

export function $createCTAButtonNode(payload: CTAButtonPayload): CTAButtonNode {
  return new CTAButtonNode(payload.text, payload.url, payload.align || 'left')
}

export function $isCTAButtonNode(node: LexicalNode | null | undefined): node is CTAButtonNode {
  return node instanceof CTAButtonNode
}
